import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  Box, Container, AppBar, Toolbar, IconButton,
  Typography, CircularProgress, Alert,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AccountCircle as AccountIcon
} from '@mui/icons-material';
import SocialMediaManager          from '../ProviderProfile/SocialMediaManager';
import { useGetBusinessByIdQuery } from '../../redux/thunks/unauth/getbusinessbyid';

export default function SocialAccountsPage() {
  const { businessId } = useParams();
  const navigate        = useNavigate();
  const location        = useLocation();
  const theme           = useTheme();
  const isMobile        = useMediaQuery(theme.breakpoints.down('md'));

  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const token = useSelector(state => state.auth?.token || null);

  const businessName           = location.state?.businessName  || 'Your Business';
  const returnPath             = location.state?.returnPath    || null;
  const placeIdFromState       = location.state?.googlePlaceId || null;

  const isAuthenticated = authStatus === 'authenticated' && token;

  // Only fetch if Place ID not passed via router state
  // Covers direct URL navigation (bookmarks, email links etc)
  const { data: profile, isLoading: profileLoading } = useGetBusinessByIdQuery(
  { businessId, authType: 'none', token },
  { skip: !businessId || placeIdFromState !== null }
);

  // Use router state if available, fall back to fetched profile
  const currentGooglePlaceId = placeIdFromState ?? profile?.googlePlaceId ?? null;

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleBack = () => {
    if (returnPath) navigate(returnPath);
    else navigate(-1);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <AccountIcon color="primary" sx={{ mr: 1 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }}>
              Integrations
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                {businessName}
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Connect your Facebook Page. If your Instagram is a business account
          linked to that Page, it connects at the same time. Revfer only posts
          drafts you approve, and you can disconnect anytime.
        </Alert>

        {/* Show loading only when fetching profile for direct navigation */}
        {profileLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SocialMediaManager
            businessId={businessId}
            businessName={businessName}
            isBusinessOwner={true}
            showAsAccordion={false}
            currentGooglePlaceId={currentGooglePlaceId}
          />
        )}
      </Container>
    </Box>
  );
}